import { getSystemId } from '@/lib/deviceId';

/**
 * Lightweight AES-GCM wrapper used by the auth layer to obfuscate credentials
 * stored in localStorage when "Keep me signed in" is checked.
 *
 * Security note (important):
 *   Symmetric encryption on the browser is not a defence against attackers
 *   who already control the machine — the key is derived from the device
 *   `systemid` which they can also read. What it *does* buy you is that the
 *   values are not present in plaintext on disk, so casual localStorage
 *   inspection, screenshots of DevTools, or unencrypted backups do not leak
 *   the actual credentials. Treat this as obfuscation, not as a vault.
 */

const SALT = new TextEncoder().encode('chanakya-erp-v1-salt');

async function deriveKey() {
  const systemid = getSystemId();
  const km = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(systemid),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: SALT, iterations: 100000, hash: 'SHA-256' },
    km,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function toBase64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function fromBase64(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export async function encryptString(plaintext) {
  if (plaintext == null) return null;
  const key = await deriveKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(String(plaintext)),
  );
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.length);
  return toBase64(combined);
}

export async function decryptString(cipher) {
  if (!cipher || typeof cipher !== 'string') return null;
  try {
    const key = await deriveKey();
    const combined = fromBase64(cipher);
    const iv = combined.slice(0, 12);
    const ct = combined.slice(12);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}
